import React, { useEffect, useMemo, useState } from 'react'
import { cn, getUserType, toCamelCase } from '@utils/safeTs'
import styles from './FlussoPersonalizzazioneCaricamentoLoghiPosizione.module.sass'
import { Button, FormField, FormTextarea, StatoOrdine, FormFile, PosizionePersonalizzazioneOrdineStatus, MyAccountImagePreview, CronologiaButton, Alert } from '@components/atoms'
import { ChevronDown, Upload, Bin, Info, CheckCircle } from '@components/icons'
import { OrdineDetailType, PersonalizzazioneOrdineType, PosizionePersonalizzazioneType, RootStateType } from '@utils/types'
import { useSelector } from 'react-redux'
import useTrans from '@hooks/useTrans'
import MyAccountStatusLogModal from '../MyAccountStatusLogModal/MyAccountStatusLogModal'
import { POSIZIONIPERSONALIZZAZIONEORDINE } from '@utils/endpoint'
import { api } from '@utils/index'
import { displayNotification } from '@components/atoms/Notifica/Notifica'
import FlussoPersonalizzazionePosizione from '../FlussoPersonalizzazionePosizione/FlussoPersonalizzazionePosizione'
import FlussoPersonalizzazionePosizioneConfigurazione from '../FlussoPersonalizzazionePosizioneConfigurazione/FlussoPersonalizzazionePosizioneConfigurazione'
import { useRouter } from 'next/router'
import { EDITOR_FILE_EXTENSIONS } from '@utils/editor-bozze'
import FlussoPersonalizzazionePosizioneLoghi from '../FlussoPersonalizzazionePosizioneLoghi/FlussoPersonalizzazionePosizioneLoghi'
import { Form, Formik } from 'formik'


interface Props {
  posizione: PosizionePersonalizzazioneType
  personalizzazione: PersonalizzazioneOrdineType
  setPosizione?:  (v: PosizionePersonalizzazioneType) => void
  setOrdine: (p: OrdineDetailType) => void
  setStatus: ((v: '' | 'unsaved' | 'completed') => void)
  personalizzazioniOpen?: number[]
  setPersonalizzazioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  posizioniOpen: number[]
  setPosizioniOpen: React.Dispatch<React.SetStateAction<number[]>>
  className?: string
}

const FlussoPersonalizzazioneCaricamentoLoghiPosizione = (props: Props) => {

  const { 
    posizione,
    personalizzazione,
    setPosizione,
    setOrdine,
    setStatus,
    posizioniOpen,
    setPosizioniOpen,
    className = '',
  } = props
  
  const t = useTrans()
  const router = useRouter()
  const hashParam = Array.isArray(router.query?.hash) ? router.query.hash[0] : router.query?.hash
  const { utente } = useSelector((state: RootStateType) => state.utente)
  const userType = getUserType(utente)

  // cliente può modificare i loghi solo se lo stato è in LOGO_DA_CARICARE, admin e commerciale (e admin sg che sarebbe cliente) sempre (supponendo che sappiano cosa stanno facendo)
  const getIsEditable = (pos, userType) => ((['LOGO_DA_CARICARE'].includes(pos.statoPosizionePersonalizzazione) || utente?.is_staff_sg) && ['cliente'].includes(userType)) || ['admin', 'commerciale', undefined].includes(userType)
  const isEditable = useMemo(() => getIsEditable(posizione, userType), [userType, posizione])
  const lastLogoLog = useMemo(() => posizione.log.filter(l => l.stato === 'BOZZA_DA_CARICARE')?.sort((a, b) => a.created < b.created ? -1 : 1)?.[0], [posizione.log])

  const [logOpen, setLogOpen] = useState(false)
  const [edited, setEdited] = useState(false)
  const [loghiEdited, setLoghiEdited] = useState(false)

  const status = useMemo(() => edited ? 'unsaved' : '', [edited])
  const allowedExtensions = useMemo(
    () => (posizione.tecnica?.estensioniAmmesse || EDITOR_FILE_EXTENSIONS)
      .map((extension) => extension?.trim()?.toLowerCase())
      .filter(Boolean),
    [posizione.tecnica?.estensioniAmmesse]
  )
  const logoAccept = allowedExtensions.length > 0 ? allowedExtensions.map((extension) => `.${extension}`).join(',') : undefined
  const formatiConsentitiLabel = allowedExtensions.length > 0 ? allowedExtensions.map((extension) => `.${extension}`).join(', ') : ""

  const initialValues = {
    note: lastLogoLog?.note || '',
    loghi: posizione.loghi?.map(l => ({
      logo: l,
      fileLogo: undefined
    })) || [],
  }

  // Salvataggio logo
  const save = (v) => {
    let formData = new FormData()
    let logoIds = []
    Object.keys(v).forEach((k) => {
      if (k === 'loghi' && Array.isArray(v[k])) {
        v[k].forEach((logo) => {
          if (logo.logo?.pk) logoIds.push(logo.logo.pk)
          if (logo.fileLogo) formData.append('fileLogo', logo.fileLogo)
        })
        formData.append('logoIds', logoIds.join(","))
      } else {
        formData.append(k, v[k])
      }
    })
    api
      .put(`${POSIZIONIPERSONALIZZAZIONEORDINE}${posizione.id}/update_logo/`, formData, {
        params: {
          ...(hashParam ? { hash: hashParam } : {}),
        },
      })
      .then((res) => {
        const newOrdine = toCamelCase(res.data) as OrdineDetailType
        setOrdine(newOrdine)
        setLoghiEdited(false)
        setEdited(false)
        displayNotification(
          `posizione_aggiornata_${posizione.id}`, 
          t("Logo per {0} confermato", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'success'
        )
      })
      .catch((res) => {
        console.log('ko', res)
        displayNotification(
          `posizione_aggiornata_error_${posizione.id}`, 
          t("Errore durante l'aggiornamento della posizione {0}", [`${posizione.personalizzazioneNomeLavorazione} - ${posizione.posizione?.nome}`]), 
          'error'
        )
      })
  }

  useEffect(() => {
    setStatus(status)
  }, [status])

  return (
    <FlussoPersonalizzazionePosizione 
      status={status}
      posizione={posizione}
      posizioniOpen={posizioniOpen}
      setPosizioniOpen={setPosizioniOpen}
      userType={userType}
      className={cn(styles.root, posizioniOpen.includes(posizione.id) ? styles['root--open'] : '', isEditable ? styles['root--editable'] : '', className)}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={(values) => {
          save(values)
        }}
      >
        {({ values, handleChange, setFieldValue, isSubmitting }) => (
          <Form className={styles.form}>
            <FlussoPersonalizzazionePosizioneConfigurazione posizione={posizione} personalizzazione={personalizzazione} />
            <div className={styles.row}>
              <FlussoPersonalizzazionePosizioneLoghi 
                posizione={posizione}
                values={values}
                setFieldValue={setFieldValue}
                edited={edited}
                setEdited={setEdited}
                loghiEdited={loghiEdited}
                setLoghiEdited={setLoghiEdited}
                isEditable={isEditable}
                variant='loghi'
                className={cn(styles.section, styles['section--loghi'])}
              />

              <div className={cn(styles.section, styles['section--note'])}>
                <p className={styles.sectionTitle}>
                  <span>{t('Nota')}</span>
                  <CronologiaButton onClick={() => setLogOpen(true)} />
                </p>
                <div>
                  {isEditable ? (
                    <div className={styles.notaForm}>
                      <FormField className={styles.notaField}>
                        <FormTextarea
                          id={`${posizione.id}_nota_caricamento`}
                          placeholder={isEditable && (!values.loghi?.length || edited) ? t("Aggiungi una nota per il reparto grafico (facoltativa)...") : ''}
                          name="note"
                          value={values.note}
                          disabled={!values.loghi?.length && !edited}
                          onChange={(e) => {
                            setFieldValue('note', e.target.value)
                            setEdited(true)
                          }}
                        />
                      </FormField>
                      <div className={styles.ctaBox}>
                        {!edited && values.loghi?.length ? (
                          <Alert 
                            variant="success"
                            styleVariant='basic'
                            icon={<CheckCircle />}
                            placeholder={t("File caricati correttamente")}
                            size="sm"
                            className={styles.successSave}
                          /> 
                        ) : (
                          <>
                            <Button 
                              variant="primary" 
                              size="medium" 
                              label={t('Conferma loghi')}
                              disabled={!edited || !values.loghi?.length}
                              onClick={() => save(values)}
                            />
                            
                            <Alert 
                              variant="muted" 
                              styleVariant='basic' 
                              icon={<Info />} 
                              placeholder={t("Carica almeno un logo per abilitare il salvataggio e procedere")} 
                              className={styles.alertSave} 
                              size="xs"
                            />
                          </>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.formValue}>
                      {posizione.log?.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.length ? (
                        posizione.log.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.[posizione.log.filter(l => l.stato === posizione.statoPosizionePersonalizzazione)?.length - 1].note
                      ) : '-'}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Form>
        )}
      </Formik>
      <MyAccountStatusLogModal id={`status_log_modal_${posizione.id}`} open={logOpen} onClose={() => setLogOpen(false)} posizione={posizione} userType={userType} />
    </FlussoPersonalizzazionePosizione>
  )
}

export default FlussoPersonalizzazioneCaricamentoLoghiPosizione
